import { FastField, FieldProps } from 'formik'
import { isEmpty } from 'lodash'
import { useMemo } from 'react'

import {
  TPropsWithFieldProps,
  TComponentWithFieldProps,
  TComponentProps,
  TWrapperProps,
} from '~/components/formikFields/models'
import { shouldRerenderFastField } from '~/features/scenarios/utils'

/**
 * Wraps a form field component with Formik's FastField so it only re-renders
 * when this field's value/errors/touched or its dependencies change.
 *
 * @param Component - The form field component to wrap (receives fieldProps from FastField).
 *
 * The returned wrapper accepts `meta` (optional). Meta is passed to FastField and used as
 * a dependency for the internal memo. Use `meta.extraFields` (array of form field paths)
 * to re-render when those other fields change.
 */
const WrapWithFastField = <
  TProps extends TPropsWithFieldProps<TField, TForm>,
  TField,
  TForm,
>(
  Component: TComponentWithFieldProps<TProps, TField, TForm>,
) => {
  return function Wrapper({
    name,
    meta = {},
    ...restProps
  }: Omit<TComponentProps<TProps, TField, TForm>, 'fieldProps'> &
    TWrapperProps) {
    const fastFieldProps = useMemo(
      () =>
        isEmpty(meta)
          ? {}
          : {
              meta,
              shouldUpdate: shouldRerenderFastField,
            },
      [meta],
    )

    return (
      <FastField name={name} {...fastFieldProps}>
        {(fieldProps: FieldProps<TField, TForm>) => {
          return (
            <Component
              {...({
                ...restProps,
                fieldProps,
              } as unknown as TProps)}
            />
          )
        }}
      </FastField>
    )
  }
}

export default WrapWithFastField
